import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';

const COUNT = 120;
const CONNECT_DIST = 1.4;
const MAX_LINE_PAIRS = COUNT * COUNT;

const mouse = { tx: 0, ty: 0, x: 0, y: 0 };

if (typeof window !== 'undefined') {
  window.addEventListener('mousemove', (e) => {
    mouse.tx = ((e.clientX / window.innerWidth) - 0.5) * 0.55;
    mouse.ty = -((e.clientY / window.innerHeight) - 0.5) * 0.35;
  }, { passive: true });
}

function ParticleField() {
  const groupRef = useRef();
  const pointsRef = useRef();
  const linesRef = useRef();

  const { positions, velocities } = useMemo(() => {
    const positions = new Float32Array(COUNT * 3);
    const velocities = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 14;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 9;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 3;
      velocities[i * 3]     = (Math.random() - 0.5) * 0.00035;
      velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.00035;
      velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.00015;
    }
    return { positions, velocities };
  }, []);

  const linePositions = useMemo(() => new Float32Array(MAX_LINE_PAIRS * 6), []);

  useFrame(() => {
    if (groupRef.current) {
      mouse.x += (mouse.tx - mouse.x) * 0.04;
      mouse.y += (mouse.ty - mouse.y) * 0.04;
      groupRef.current.rotation.y = mouse.x;
      groupRef.current.rotation.x = mouse.y;
    }

    if (!pointsRef.current) return;
    const pos = pointsRef.current.geometry.attributes.position.array;

    for (let i = 0; i < COUNT; i++) {
      pos[i * 3]     += velocities[i * 3];
      pos[i * 3 + 1] += velocities[i * 3 + 1];
      pos[i * 3 + 2] += velocities[i * 3 + 2];
      if (Math.abs(pos[i * 3])     > 7)   velocities[i * 3]     *= -1;
      if (Math.abs(pos[i * 3 + 1]) > 4.5) velocities[i * 3 + 1] *= -1;
      if (Math.abs(pos[i * 3 + 2]) > 1.5) velocities[i * 3 + 2] *= -1;
    }
    pointsRef.current.geometry.attributes.position.needsUpdate = true;

    if (!linesRef.current) return;
    const linePos = linesRef.current.geometry.attributes.position.array;
    let idx = 0;
    const maxIdx = linePos.length - 6;

    for (let i = 0; i < COUNT && idx < maxIdx; i++) {
      for (let j = i + 1; j < COUNT && idx < maxIdx; j++) {
        const dx = pos[i*3]   - pos[j*3];
        const dy = pos[i*3+1] - pos[j*3+1];
        const dz = pos[i*3+2] - pos[j*3+2];
        if (dx*dx + dy*dy + dz*dz < CONNECT_DIST * CONNECT_DIST) {
          linePos[idx++] = pos[i*3];   linePos[idx++] = pos[i*3+1]; linePos[idx++] = pos[i*3+2];
          linePos[idx++] = pos[j*3];   linePos[idx++] = pos[j*3+1]; linePos[idx++] = pos[j*3+2];
        }
      }
    }
    for (let i = idx; i < linePos.length; i++) linePos[i] = 0;
    linesRef.current.geometry.setDrawRange(0, idx / 3);
    linesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <group ref={groupRef}>
      <points ref={pointsRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" array={positions} count={COUNT} itemSize={3} />
        </bufferGeometry>
        <pointsMaterial size={0.025} color="#B45309" transparent opacity={0.45} sizeAttenuation />
      </points>
      <lineSegments ref={linesRef}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" array={linePositions} count={linePositions.length / 3} itemSize={3} />
        </bufferGeometry>
        <lineBasicMaterial color="#D97706" transparent opacity={0.09} />
      </lineSegments>
    </group>
  );
}

const ParticleBackground = () => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
    <Canvas camera={{ position: [0, 0, 6], fov: 70 }} gl={{ antialias: false, alpha: true }} dpr={[1, 1.5]}>
      <ParticleField />
    </Canvas>
  </div>
);

export default ParticleBackground;
